import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';

export type SessionUser = {
  id: number;
  username: string;
  displayName: string;
  isAdmin: boolean;
  lastLoginAt: string | null;
};

export type ManagedUser = SessionUser & { createdAt: string };

export type Session = {
  internal: boolean;
  authenticated: boolean;
  requiresLogin: boolean;
  user: SessionUser | null;
  canManageUsers: boolean;
  userCount: number;
};

const API_BASE = '/api';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);

  readonly session = signal<Session | null>(null);
  /** Stays false until the first /session response, so we never flash the login screen. */
  readonly loaded = signal(false);

  readonly requiresLogin = computed(() => this.session()?.requiresLogin === true);
  readonly canManageUsers = computed(() => this.session()?.canManageUsers === true);
  readonly currentUser = computed(() => this.session()?.user ?? null);
  readonly isInternal = computed(() => this.session()?.internal === true);
  readonly userCount = computed(() => this.session()?.userCount ?? 0);

  loadSession(): Observable<Session> {
    return this.http.get<Session>(`${API_BASE}/auth/session`).pipe(
      tap(session => {
        this.session.set(session);
        this.loaded.set(true);
      }),
    );
  }

  login(username: string, password: string, rememberMe: boolean): Observable<unknown> {
    return this.http.post(`${API_BASE}/auth/login`, { username, password, rememberMe });
  }

  logout(): Observable<unknown> {
    return this.http.post(`${API_BASE}/auth/logout`, {});
  }

  /** Called by the HTTP interceptor when the backend rejects a request as unauthenticated. */
  markSessionExpired(): void {
    const current = this.session();
    if (!current || current.requiresLogin) return;
    this.session.set({
      ...current,
      authenticated: false,
      requiresLogin: true,
      user: null,
      canManageUsers: false,
    });
  }

  listUsers(): Observable<ManagedUser[]> {
    return this.http.get<ManagedUser[]>(`${API_BASE}/users`);
  }

  createUser(data: {
    username: string;
    password: string;
    displayName: string;
    isAdmin: boolean;
  }): Observable<ManagedUser> {
    return this.http.post<ManagedUser>(`${API_BASE}/users`, data);
  }

  updateUser(
    id: number,
    data: { displayName?: string; password?: string; isAdmin?: boolean },
  ): Observable<ManagedUser> {
    return this.http.patch<ManagedUser>(`${API_BASE}/users/${id}`, data);
  }

  deleteUser(id: number): Observable<unknown> {
    return this.http.delete(`${API_BASE}/users/${id}`);
  }
}
